/**
 * Just enough JPEG and EXIF reading to (a) suggest the town a photo was taken in, on the device, and (b) prove
 * the file we upload carries no EXIF block. The GPS position never leaves the phone: the upload is a re-encode
 * without metadata (docs/50 §4.5 safety screen, docs/53 §7.8 strips again on the server).
 */

export interface Gps {
  lat: number;
  lng: number;
}

function isJpeg(v: DataView): boolean {
  return v.byteLength > 4 && v.getUint16(0) === 0xffd8;
}

/** Offset of the APP1 "Exif" segment's TIFF header, or -1. */
function exifTiff(v: DataView): number {
  if (!isJpeg(v)) return -1;
  let off = 2;
  while (off + 4 <= v.byteLength) {
    const marker = v.getUint16(off);
    if ((marker & 0xff00) !== 0xff00) return -1;
    const len = v.getUint16(off + 2);
    if (marker === 0xffe1 && off + 10 <= v.byteLength && v.getUint32(off + 4) === 0x45786966 /* Exif */) return off + 10;
    if (marker === 0xffda /* start of scan */) return -1;
    off += 2 + len;
  }
  return -1;
}

export function hasExif(buf: ArrayBuffer): boolean {
  return exifTiff(new DataView(buf)) >= 0;
}

export function readGps(buf: ArrayBuffer): Gps | null {
  const v = new DataView(buf);
  const tiff = exifTiff(v);
  if (tiff < 0) return null;
  try {
    const le = v.getUint16(tiff) === 0x4949;
    const u16 = (o: number) => v.getUint16(tiff + o, le);
    const u32 = (o: number) => v.getUint32(tiff + o, le);
    const ifd0 = u32(4);
    const entries = (ifd: number) => Array.from({ length: u16(ifd) }, (_, i) => ifd + 2 + i * 12);
    const gpsEntry = entries(ifd0).find((e) => u16(e) === 0x8825);
    if (gpsEntry === undefined) return null;
    const gpsIfd = u32(gpsEntry + 8);
    const tags: Record<number, number> = {};
    for (const e of entries(gpsIfd)) tags[u16(e)] = e;
    const ref = (tag: number) => (tags[tag] !== undefined ? String.fromCharCode(v.getUint8(tiff + tags[tag]! + 8)) : '');
    const rational3 = (tag: number) => {
      const e = tags[tag];
      if (e === undefined) return null;
      const at = u32(e + 8);
      const r = (i: number) => u32(at + i * 8) / (u32(at + i * 8 + 4) || 1);
      return r(0) + r(1) / 60 + r(2) / 3600;
    };
    const lat = rational3(2);
    const lng = rational3(4);
    if (lat === null || lng === null || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat: ref(1) === 'S' ? -lat : lat, lng: ref(3) === 'W' ? -lng : lng };
  } catch {
    return null;
  }
}

/** Miles between two points (haversine). */
export function miles(a: Gps, b: Gps): number {
  const R = 3958.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** The closest of their towns within 15 miles, or null. */
export function nearestTown<T extends { center: Gps | null }>(gps: Gps, towns: T[], maxMiles = 15): T | null {
  let best: { t: T; d: number } | null = null;
  for (const t of towns) {
    if (!t.center) continue;
    const d = miles(gps, t.center);
    if (d <= maxMiles && (!best || d < best.d)) best = { t, d };
  }
  return best?.t ?? null;
}

/** Target size for the 2,400 px long edge (docs/52 §8). */
export function fitLongEdge(w: number, h: number, max = 2400): { w: number; h: number } {
  const long = Math.max(w, h);
  if (long <= max) return { w, h };
  const k = max / long;
  return { w: Math.round(w * k), h: Math.round(h * k) };
}
